import type { EventDetails, LinkedTG } from '@/api/Helpers'
import type { ApplyCodePayload, ReserveTicketPayload } from '@/api/types/payloads'
import { i18n } from '@/app/I18n'
import CartChangesNormalizer from '@/helpers/CartChangesNormalizer'

export interface CartChanges {
  create?: true
  campaign?: Dictionary
  remove?: string[]
  promoCodesToApplyBeforeReserve?: ApplyCodeInModify
  event?: EventDetails
  ignoreFeeTickets?: true
  add?: ReserveTicketPayload[]
  additionalInfo?: AdditionalTicketInfoPayload[]
  promoCodes?: string[]
}

// Properties are specified in the same order that the API endpoint executes them, for convenience.
export interface ModifyCartPayload {
  create?: {
    channel_id: 'web'
    campaign?: Dictionary
  }
  remove?: {
    tickets: string[]
  }
  apply_code_before?: ApplyCodeInModify
  reserve?: {
    tickets: ReserveTicketPayload[]
    additional_info?: AdditionalTicketInfoPayload[]
  }
  apply_code?: ApplyCodeInModify
}

export interface AdditionalTicketInfoPayload {
  ticket_group_id: string
  data: Dict<Primitive>
}

export interface ApplyCodeInModify extends ApplyCodePayload {
  ignore_errors?: boolean
}

export function reserveTicketsPayload(
  quantities: TicketTypeQuantities,
  session?,
  details: Dict<EventAdmitDetails[]> = {},
  data: Dict<ReserveTicketPayload['handler_data'][]> = {},
): ReserveTicketPayload[] {
  const tickets: ReserveTicketPayload[] = []

  for (const [id, { quantity, price }] of Object.entries(quantities)) {
    for (let i = 0; i < quantity; i++) {
      tickets.push({
        ticket_type_id: id,
        face_value: price,
        // `undefined` not `null`, so that the property is completely omitted from the JSON payload.
        event_session_id: session ? session.id : undefined,
        admit_name: details[id] ? details[id][i].name : undefined,
        admit_email: details[id] ? details[id][i].email : undefined,
        handler_data: data[id] ? data[id][i] : undefined,
      })
    }
  }

  return tickets
}

export function maxOneTicket(groups: LinkedTG[]): boolean {
  return groups.length === 1 && groups[0].types.length === 1 && groups[0].max_tickets_per_order === 1
}

export function getButtonLabel(
  flexibleTickets: boolean,
  giftOfMembership: boolean,
  totalSelectedTickets: number,
): string {
  if (flexibleTickets) {
    if (giftOfMembership) {
      return i18n.tc(`reserve.buttonLabel.member`)
    } else {
      return i18n.tc(`reserve.buttonLabel.passes`, totalSelectedTickets)
    }
  } else {
    return i18n.tc(`reserve.buttonLabel.tickets`, totalSelectedTickets)
  }
}

export function cartPayload(
  changes: CartChanges,
  tickets: Ticket[],
  ticketGroups: LinkedTG[],
  eventTemplates: EventWithMeta[],
  preloadedPromoCode?: string,
): ModifyCartPayload {
  const events = [...eventTemplates]
  if (changes.event) {
    events.push(changes.event)
  }
  const groups = [...ticketGroups, ...(changes.event?.ticketGroups ?? [])]
  return new CartChangesNormalizer(tickets, groups, events).normalize(changes, preloadedPromoCode)
}
